import React, { ReactElement } from "react";
import { ReactComponent as FileBlue } from "../../images/file_blue.svg";
import { ReactComponent as FilePurplePending } from "../../images/file_purple_pending.svg";
import { ReactComponent as FileGreenCheck } from "../../images/file_green_check.svg";
import { ReactComponent as CheckBlueCircle } from "../../images/check_blue_circle.svg";
import { ReactComponent as CalendarDotsBlue } from "../../images/calendar-dots_blue.svg";
import { ReactComponent as ExclamationTriangleRed } from "../../images/exclamation-triangle_red.svg";
import { ReactComponent as ClockYellow } from "../../images/clock_yellow.svg";
import Loading from "../loading/loading";
import { Popover } from "../popover/popover";
import "./metricSummaryTile.scss";

export type MetricSummaryTileIconPreset =
  | "fileBlue"
  | "filePurplePending"
  | "fileGreenCheck"
  | "checkBlueCircle"
  | "calendarBlue"
  | "clockYellow"
  | "exclamationRed";

export type MetricSummaryTileColorVariant = "blue" | "purple" | "green" | "yellow" | "red";

export interface MetricSummaryTileProps {
  isLoading: boolean;
  title: string;
  value: string | number | ReactElement;
  legendDescription: string;
  onApply?: () => void;
  iconPreset?: MetricSummaryTileIconPreset;
  icon?: ReactElement;
  colorVariant?: MetricSummaryTileColorVariant;
}

const ICON_PRESETS: Record<MetricSummaryTileIconPreset, ReactElement> = {
  fileBlue: <FileBlue />,
  filePurplePending: <FilePurplePending />,
  fileGreenCheck: <FileGreenCheck />,
  checkBlueCircle: <CheckBlueCircle />,
  calendarBlue: <CalendarDotsBlue />,
  clockYellow: <ClockYellow />,
  exclamationRed: <ExclamationTriangleRed />,
};

const getTileIcon = (
  iconPreset?: MetricSummaryTileIconPreset,
  icon?: ReactElement
): ReactElement => {
  if (icon) return icon;
  if (iconPreset) return ICON_PRESETS[iconPreset];
  return <></>;
};

export const MetricSummaryTile: React.FC<MetricSummaryTileProps> = ({
  isLoading,
  title,
  value,
  legendDescription,
  onApply,
  iconPreset,
  icon,
  colorVariant = "blue",
}) => (
  <div className="xgs-metric-summary-tile">
    <div className={`xgs-metric-summary-tile__icon-container --color-${colorVariant}`}>
      {getTileIcon(iconPreset, icon)}
    </div>
    <div className="xgs-metric-summary-tile__content">
      <div className="xgs-metric-summary-tile__content__values">
        {isLoading ? (
          <Loading isLoading />
        ) : (
          <div className="xgs-metric-summary-tile__content__value">{value}</div>
        )}
        <Popover
          values={<div className="terminal-summary__tooltip">{legendDescription}</div>}
        />
      </div>
      <div className="xgs-metric-summary-tile__content__title-row">
        <div className="xgs-metric-summary-tile__content__title">{title}</div>
        {!!onApply && !isLoading && (
          <button
            type="button"
            className="xgs-metric-summary-tile__content__apply-btn"
            onClick={onApply}
          >
            Apply
          </button>
        )}
      </div>
    </div>
  </div>
);

export default MetricSummaryTile;
